import { Inject, Injectable, Optional } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import {
	AppSettingsService,
	CustomWindow,
	FeatureService,
	HistoryService,
	LoggingService,
	ShopperService,
	ShopperState,
	TRADER_BASE_URL,
	WINDOW,
} from '@woolworthsnz/styleguide';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationService } from '../services';
import { ContextResponse } from '@woolworthsnz/trader-api';

/*
  This guard is for auth pages like Login, register. When user comes to these pages as logged in user it redirects them to account details.

	NOTE: Side-effects. This guard cannot return a URLTree until URLTrees support the navigationExtras option of replaceUrl. While it continues to use navigateByUrl it introduces side-effect that mean it should not be used with other guards in parallel.
*/
@Injectable({
	providedIn: 'root',
})
export class AuthPageGuard {
	constructor(
		private shopperService: ShopperService,
		private historyService: HistoryService,
		private authService: AuthenticationService,
		private appSettingsService: AppSettingsService,
		private router: Router,
		private loggingService: LoggingService,
		private featureService: FeatureService,
		@Inject(WINDOW) public window: CustomWindow,
		@Optional() @Inject(TRADER_BASE_URL) private traderBaseUrl: string
	) {}

	canActivate(route?: ActivatedRouteSnapshot): Observable<boolean> {
		return this.shopperService.state$.pipe(
			map((s: ShopperState) => {
				// A logged In user can be a shopper or non shopper example brand site user, as we use SSO any user from brunch,
				// pharmacy, or brandsite can login to shop website

				if (s.isShopper && s.isLoggedIn && !route?.data.loggedOut) {
					// if there is a return url on the url use that - other wise previous
					if (route?.queryParams.returnUrl) {
						this.router.navigateByUrl(this.stripAuthReferrer(route.queryParams.returnUrl), {
							replaceUrl: true,
						});
					} else {
						this.router.navigateByUrl(this.historyService.getPreviousUrl(), { replaceUrl: true });
					}
					return false;
				}

				// if the route demands the user be logged out - log the user out but allow the route to load
				if (route?.data.loggedOut && s.isLoggedIn) {
					this.authService.doLogout();
					return true;
				}

				if (!s.isShopper && s.isLoggedIn) {
					this.router.navigateByUrl('/shop/register/shopperdetails', { replaceUrl: true });
					return false;
				}

				if (this.appSettingsService.useSSU()) {
					const encodedRedirectUrl = `${encodeURIComponent(
						this.appSettingsService.getAuthRedirectUrl(this.window.location.href)
					)}`;
					const url = `${this.appSettingsService.getEndpoint(
						'oidcSignInEndpoint'
					)}?redirectUrl=${encodedRedirectUrl}`;

					if (this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.RedirectValidation)) {
						this.loggingService.trackEvent('RedirectValidation', {
							shopper: this.shopperService.state?.shopperScvId || 'unknown',
							detail: 'track redirect in Auth page guard',
							href: url,
						});
					}

					this.window.location.href = url;

					return false;
				}
				return true;
			})
		);
	}

	// This is a hangover from legacy redirect when auth was handled in legacy. Now that legacy redirects to login on angular if the authreferrer is preserved we would redirect back to the legacy page protected by auth which would redirect to the auth referrer - probably undesirable.
	stripAuthReferrer = (url: string): string => {
		const parsedUrl = this.router.parseUrl(url);

		if (parsedUrl.queryParams.authReferrer) {
			delete parsedUrl.queryParams.authReferrer;
		}

		return this.router.serializeUrl(parsedUrl);
	};
}
